import { Box, Button, ButtonBase, Stack, Typography } from '@mui/material';
import { createContext, useContext, useState } from 'react';
import Iconify from 'src/components/iconify';

export type PageInfoStep = {
  image?: string;
  title: string;
  text: string;
};

// provided by PageHeader's info drawer so the guide's last step can close it
export const PageInfoCloseContext = createContext<VoidFunction | null>(null);

// the step-by-step guide inside the page-header info panel; written in simple
// English on purpose, many drivers and dispatchers are not native speakers
export default function PageInfoCarousel({ steps }: { steps: PageInfoStep[] }) {
  const [index, setIndex] = useState(0);
  const step = steps[index];
  const onClose = useContext(PageInfoCloseContext);
  const lastStep = index === steps.length - 1;

  return (
    // fills the info drawer and pins the navigation to the bottom, so Back and
    // Next stay under the cursor while clicking through steps of any size
    <Stack direction="column" gap={2} sx={{ height: '100%' }}>
      <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
        <Stack direction="column" gap={2}>
          {step.image && (
            // the border hugs the image itself; leftover space from the fixed
            // frame stays outside the border, not inside it
            <Box
              sx={{
                height: 260,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                component="img"
                src={step.image}
                alt={step.title}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              />
            </Box>
          )}

          <div>
            <Typography variant="overline" color="text.disabled">
              Step {index + 1} of {steps.length}
            </Typography>
            <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
              {step.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
              {step.text}
            </Typography>
          </div>
        </Stack>
      </Box>

      <Stack direction="row" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
        <Button
          variant="outlined"
          size="small"
          disabled={index === 0}
          onClick={() => setIndex(index - 1)}
          startIcon={<Iconify icon="eva:arrow-back-fill" />}
        >
          Back
        </Button>

        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', gap: 0.75 }}>
          {steps.map((_, dotIndex) => (
            <ButtonBase
              key={dotIndex}
              onClick={() => setIndex(dotIndex)}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: dotIndex === index ? 'text.primary' : 'action.selected',
              }}
            />
          ))}
        </Box>

        <Button
          variant="contained"
          size="small"
          onClick={() => {
            if (lastStep) {
              onClose?.();
              setIndex(0);
            } else {
              setIndex(index + 1);
            }
          }}
          endIcon={lastStep ? <Iconify icon="eva:checkmark-fill" /> : <Iconify icon="eva:arrow-forward-fill" />}
        >
          {lastStep ? 'Done' : 'Next'}
        </Button>
      </Stack>
    </Stack>
  );
}
